import React, { forwardRef } from 'react';
import { Checkbox as AntdCheckbox } from 'antd';
import type { CheckboxProps as AntdCheckboxProps, CheckboxRef } from 'antd';
import styled from 'types/theme.types';
import omit from 'lodash/omit';

/**
 * Two states the bridge cannot carry: antd draws no fill on an unchecked box's hover and takes the hover
 * border from `colorPrimary`, the checked fill; and it reuses the resting `colorBorder` when disabled.
 *
 * Both out-specify antd without `!important` — six classes against four, three against two. Re-derive
 * those counts rather than trusting them if the selectors change.
 *
 * Checked is excluded because the label text sits outside antd's wrapper: hovering it would beat antd's
 * two-class checked fill while its six-class checked-hover rule (which needs the wrapper itself hovered)
 * never fires, blanking the box. Indeterminate stays in — its resting fill is the container background.
 */
// must be `label` to bind text to checkbox input
const OuterCheckboxContainer = styled.label`
  display: flex;

  gap: var(--spacing-xxs);
  align-items: center;

  &:hover .ant-checkbox:not(.ant-checkbox-disabled):not(.ant-checkbox-checked) .ant-checkbox-inner {
    background-color: var(--surface-primary-background);
    border-color: var(--icon-action);
  }
`;

const CheckboxTextContainer = styled.span`
  display: flex;

  font-size: var(--font-size-body-md);
  line-height: var(--line-height-body-md);
  font-weight: var(--font-weight-light);
  color: var(--text-heading);
  align-items: center;
`;

const Checkbox = forwardRef<CheckboxRef, AntdCheckboxProps>(({ children, ...props }, ref) => {
  return (
    <OuterCheckboxContainer>
      <AntdCheckbox ref={ref} {...omit(props, ['hideError'])} />
      <CheckboxTextContainer>{children}</CheckboxTextContainer>
    </OuterCheckboxContainer>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
